import { useState, useCallback, useRef, useEffect } from 'react';

interface UseScrollMarginOptions {
	/**
	 * The ID of the scroll container element
	 * @default 'AkMainContent'
	 */
	scrollContainerId?: string;
	/**
	 * The test ID of the table container element
	 * @default 'table-container'
	 */
	tableContainerTestId?: string;
	/**
	 * Direct reference to the table element (preferred over test ID)
	 */
	tableElement?: HTMLElement | null;
}

interface UseScrollMarginResult {
	/**
	 * The calculated scroll margin value
	 */
	scrollMargin: number;
	/**
	 * Function to manually update the scroll margin
	 */
	updateScrollMargin: () => void;
	/**
	 * Ref callback to attach to the table element
	 */
	tableRef: (element: HTMLElement | null) => void;
}

/**
 * Custom hook to calculate and manage scroll margin based on element positions
 *
 * @param options Configuration options for the hook
 * @returns Object containing scrollMargin value and updateScrollMargin function
 */
export const useScrollMargin = ({
	scrollContainerId = 'AkMainContent',
	tableContainerTestId = 'table-container',
	tableElement,
}: UseScrollMarginOptions = {}): UseScrollMarginResult => {
	const parentRef = useRef<HTMLElement | null>(null);
	const tableRef = useRef<HTMLElement | null>(null);
	const resizeObserverRef = useRef<ResizeObserver>();
	const [scrollMargin, setScrollMargin] = useState(0);

	const getScrollElement = useCallback(() => {
		const element = document.getElementById(scrollContainerId);
		parentRef.current = element;
		return element;
	}, [scrollContainerId]);

	const updateScrollMargin = useCallback(() => {
		if (!parentRef.current || !tableRef.current) {
			return;
		}

		const parentRect = parentRef.current.getBoundingClientRect();
		const tableRect = tableRef.current.getBoundingClientRect();

		const calculatedScrollMargin = tableRect.top - parentRect.top;

		setScrollMargin(Math.max(0, calculatedScrollMargin));
	}, []);

	// Table ref callback for direct element reference
	const tableRefCallback = useCallback(
		(element: HTMLElement | null) => {
			tableRef.current = element;
			updateScrollMargin(); // Update when table ref changes
		},
		[updateScrollMargin],
	);

	// Set table ref on mount (fallback to querySelector if no direct element provided)
	useEffect(() => {
		if (tableElement) {
			tableRef.current = tableElement;
		} else {
			//TODO: Change the approach to work for multiple tables on one page
			const foundElement = document.querySelector(`[data-testid="${tableContainerTestId}"]`);
			if (foundElement && foundElement instanceof HTMLElement) {
				tableRef.current = foundElement;
			} else {
				tableRef.current = null;
			}
		}
	}, [tableContainerTestId, tableElement]);

	// Set up ResizeObserver and initial scroll margin calculation
	useEffect(() => {
		// Initialize parent ref
		getScrollElement();

		const observer = new ResizeObserver(() => {
			updateScrollMargin();
		});

		resizeObserverRef.current = observer;

		// Observe both parent and table elements
		if (parentRef.current) {
			observer.observe(parentRef.current);
		}
		if (tableRef.current) {
			observer.observe(tableRef.current);
		}

		// Initial update
		updateScrollMargin();

		return () => {
			observer.disconnect();
		};
	}, [updateScrollMargin, getScrollElement]);

	return {
		scrollMargin,
		updateScrollMargin,
		tableRef: tableRefCallback,
	};
};
